/**
 * Shared types for Formency UI illustrations.
 *
 * These are miniature wireframe renderings of actual Formency interfaces —
 * session player, create box, course structure, etc. Each illustration
 * supports two rendering contexts via the `variant` prop.
 */

export type IllustrationVariant = 'on-gradient' | 'on-surface';

export interface IllustrationProps {
  /** Rendering context: 'on-gradient' for gradient backgrounds, 'on-surface' for light/dark surfaces. */
  variant?: IllustrationVariant;
  /** Optional CSS class name. */
  className?: string;
  /** Optional inline styles. */
  style?: React.CSSProperties;
}

// ─── Color palettes ─────────────────────────────────────────────────────────

interface Palette {
  frame: string;
  frameFill: string;
  frameStroke: string;
  strong: string;
  medium: string;
  subtle: string;
  faint: string;
  accent1: string;
  accent2: string;
  accent3: string;
  cardFill: string;
  cardStroke: string;
  buttonFill: string;
  buttonText: string;
}

const GRADIENT_PALETTE: Palette = {
  frame: 'rgba(255,255,255,0.06)',
  frameFill: 'rgba(255,255,255,0.06)',
  frameStroke: 'rgba(255,255,255,0.12)',
  strong: 'rgba(255,255,255,0.65)',
  medium: 'rgba(255,255,255,0.2)',
  subtle: 'rgba(255,255,255,0.14)',
  faint: 'rgba(255,255,255,0.08)',
  accent1: 'rgba(255,170,120,0.7)',
  accent2: 'rgba(255,130,160,0.6)',
  accent3: 'rgba(155,107,218,0.5)',
  cardFill: 'rgba(255,255,255,0.05)',
  cardStroke: 'rgba(255,255,255,0.15)',
  buttonFill: 'rgba(255,255,255,0.12)',
  buttonText: 'rgba(255,255,255,0.25)',
};

const SURFACE_PALETTE: Palette = {
  frame: 'var(--color-surface-muted, rgba(0,0,0,0.03))',
  frameFill: 'var(--color-surface-muted, rgba(0,0,0,0.03))',
  frameStroke: 'var(--color-border, rgba(0,0,0,0.09))',
  strong: 'var(--color-text, #1c1c1e)',
  medium: 'var(--color-text-muted, #918da0)',
  subtle: 'var(--color-border, #e4e1ec)',
  faint: 'var(--color-surface-track, rgba(0,0,0,0.05))',
  accent1: 'var(--color-accent, #9b6bda)',
  accent2: 'rgba(255,130,160,0.8)',
  accent3: 'rgba(100,180,220,0.7)',
  cardFill: 'var(--color-surface, rgba(255,255,255,0.8))',
  cardStroke: 'var(--color-surface-border, rgba(0,0,0,0.09))',
  buttonFill: 'var(--color-accent, #9b6bda)',
  buttonText: 'var(--color-text-on-accent, #ffffff)',
};

export function usePalette(variant: IllustrationVariant = 'on-gradient'): Palette {
  return variant === 'on-gradient' ? GRADIENT_PALETTE : SURFACE_PALETTE;
}
